import { ApiError, type ErrorCode } from '@dpem/contracts';

/**
 * Turning a failure into something a person can act on.
 *
 * The API's envelope already carries a human message, and for domain failures
 * that message is the better one — it knows which gate was unreachable. What it
 * cannot know is what the *user* should do next, and for a handful of codes that
 * advice is the only useful part. So: code-specific guidance where we have it,
 * the server's own message where we do not, and never a bare stack trace.
 *
 * `traceId` is surfaced on unexpected failures only. It is the one thing that
 * makes a support conversation short, and it is meaningless noise on a
 * validation error the user can just fix.
 */

/** Advice, not restatement: what to do, for the codes where an action exists. */
const GUIDANCE: Partial<Record<ErrorCode, string>> = {
  UNAUTHENTICATED: 'Your session has ended. Sign in again to continue.',
  FORBIDDEN: 'Your role does not include this action. Ask an administrator for access.',
  REVISION_CONFLICT:
    'Someone else saved a change to this method first. Reload to see their version, then reapply yours.',
  CONFLICT: 'That change collides with something already saved. Reload and try again.',
  NOT_FOUND: 'That item no longer exists. It may have been deleted or never published.',
  ELEMENT_LOCKED: 'Another author is editing this element. Their lock releases when they finish.',
  IMMUTABLE: 'This version is published, so it cannot be edited. Derive a new version instead.',
  RATE_LIMITED: 'Too many requests. Wait a moment and try again.',
  ASSIST_UNAVAILABLE:
    'AI assistance is not available. An administrator can check the Bedrock connection in Admin.',
  ASSIST_CANCELLED: 'The suggestion was cancelled. Nothing was applied to the method.',
  ASSIST_BUDGET_EXCEEDED: 'The AI assistance budget for this period is spent.',
  CREDENTIAL_INTEGRITY_FAILURE:
    'A stored credential could not be opened. An administrator needs to re-enter it in Admin.',
  PAYLOAD_TOO_LARGE: 'That file is larger than the platform accepts.',
  DEPENDENCY_UNAVAILABLE:
    'A service the platform depends on is not responding. Nothing was saved; try again shortly.',
  NETWORK: 'The platform could not be reached. Check your connection and try again.',
  REQUEST_ABORTED: 'The request was cancelled.',
  CONTRACT_VIOLATION:
    'The platform returned data this version of the app does not understand. Reload to pick up the current version.',
  INTERNAL: 'Something failed on the platform. Nothing was saved.',
};

/** Codes whose own message is more specific than any advice we could write. */
const PREFER_SERVER_MESSAGE: readonly ErrorCode[] = [
  'VALIDATION_FAILED',
  'ONTOLOGY_INVALID',
  'INVARIANT_VIOLATION',
  'IMPORT_FAILED',
  'ALREADY_EXISTS',
  'INVALID_STATE',
  'UNSUPPORTED_MEDIA_TYPE',
];

export interface PresentedError {
  readonly title: string;
  readonly detail: string | null;
  /** Shown only when the user cannot fix it themselves. */
  readonly traceId: string | null;
  readonly code: ErrorCode | 'UNKNOWN';
  /** Whether retrying the same request could plausibly succeed. */
  readonly retryable: boolean;
}

const RETRYABLE: readonly ErrorCode[] = [
  'NETWORK',
  'RATE_LIMITED',
  'DEPENDENCY_UNAVAILABLE',
  'INTERNAL',
];

const SHOW_TRACE: readonly ErrorCode[] = [
  'INTERNAL',
  'DEPENDENCY_UNAVAILABLE',
  'CONTRACT_VIOLATION',
];

/**
 * Values that mean "there is no trace", from both ends. `unknown` is what the client's own
 * `ApiError` falls back to when no response arrived; `untraced` is the API's sentinel for a
 * failure raised before its trace middleware ran. Either shown to a user is worse than showing
 * nothing: they quote a support reference that correlates to no log line anywhere.
 */
const NO_TRACE: readonly string[] = ['unknown', 'untraced'];

export const presentError = (error: unknown): PresentedError => {
  if (ApiError.is(error)) {
    const serverFirst = PREFER_SERVER_MESSAGE.includes(error.code);
    const guidance = GUIDANCE[error.code];
    return {
      title: serverFirst ? error.message : (guidance ?? error.message),
      detail: serverFirst ? (guidance ?? null) : guidance === undefined ? null : error.message,
      traceId:
        SHOW_TRACE.includes(error.code) && !NO_TRACE.includes(error.traceId)
          ? error.traceId
          : null,
      code: error.code,
      retryable: RETRYABLE.includes(error.code),
    };
  }

  // Not an ApiError: a bug in our own code, a thrown string, a rejected promise
  // from a third-party library. Say so plainly rather than dressing it up.
  return {
    title: 'Something went wrong in the app.',
    detail: error instanceof Error ? error.message : null,
    traceId: null,
    code: 'UNKNOWN',
    retryable: false,
  };
};

/**
 * Whether React Query should retry. Only the transport-ish failures: retrying a
 * 403 cannot change the answer, and retrying a 409 would race the user.
 */
export const shouldRetryQuery = (failureCount: number, error: unknown): boolean => {
  if (failureCount >= 2) return false;
  if (!ApiError.is(error)) return false;
  return RETRYABLE.includes(error.code) && error.code !== 'RATE_LIMITED';
};
